import { useRef, useState, useSyncExternalStore, type KeyboardEvent, type PointerEvent, type ReactElement, type Ref } from "react";
import { logical } from "../../ui/kit/index.ts";
import type { HudArea } from "../hud/slots.ts";
import { dragChatSize, keyChatSize, sizeLabel, type ChatSize } from "./size.ts";
import { say } from "../../words.ts";
import { pageKeyHints } from "../touch/key-hints.ts";

/**
 * The chat panel's resize grip (P1.9-T42, ADR-061's amendment "the chat panel resizes"; the design's grip, a visual
 * reference): a 12 square control at the tab row's end, the panel's top-right corner, drawing six dots. A drag
 * resizes the panel from that corner, the pointer captured for the drag alone, so nothing listens while the panel
 * rests; a press of the pointer takes no focus. Focused by the keyboard it shows the focus ring, and the arrow keys
 * resize a pixel at a time, 8 with Shift, the only keys it takes (ADR-061's amendment of 2026-10-03), each
 * prevented so neither the input router nor the game acts on it. While it is dragged or focused the panel shows its
 * size, in a status region assistive technology reads as it changes.
 */

/**
 * The grip's side in logical px, the design's.
 */
export const GRIP_SIZE = 12;

/**
 * The grip's dots, each 2 square, by its left and top in the grip in logical px: the design's six, a triangle in
 * the top-right corner.
 */
const DOTS: readonly (readonly [number, number])[] = [
  [8, 2],
  [8, 5],
  [5, 2],
  [8, 8],
  [5, 5],
  [2, 2],
];

/**
 * A dot's side in logical px.
 */
const DOT = 2;

/**
 * The id of the element showing the size, which the grip names as its description while it is used.
 */
export const SIZE_LABEL_ID = "ot-chat-size";

/**
 * How far right of the panel the size is shown, the design's.
 */
export const SIZE_LABEL_GAP = 3;

/**
 * The grip.
 *
 * @param props The `wish`, the size kept, of which the panel draws what fits; the `area` the panel is on; `onResize`
 *   with a new wish and whether to keep it (false while a drag is under way); `onActive`, told when the grip starts
 *   and stops being dragged or focused, so the panel shows its size and ring; and where the caller keeps the grip to
 *   know when it has the focus (`gripRef`).
 * @returns The grip.
 */
export function ChatGrip({ wish, area, onResize, onActive, gripRef }: { readonly wish: ChatSize; readonly area: HudArea; readonly onResize: (size: ChatSize, keep: boolean) => void; readonly onActive: (active: boolean) => void; readonly gripRef?: Ref<HTMLButtonElement> | undefined }): ReactElement {
  const drag = useRef<{ readonly pointer: number; readonly x: number; readonly y: number; readonly px: number; readonly wish: ChatSize; last: ChatSize } | null>(null);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  /**
   * Starts a drag from the wish as it stands, and keeps focus where it was.
   *
   * @param event The press.
   */
  const onPointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    const px = event.currentTarget.getBoundingClientRect().width / GRIP_SIZE;
    if (!(px > 0)) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointer: event.pointerId, x: event.clientX, y: event.clientY, px, wish, last: wish };
    setDragging(true);
    onActive(true);
  };
  /**
   * Resizes with the pointer's travel while a drag is under way.
   *
   * @param event The move.
   */
  const onPointerMove = (event: PointerEvent<HTMLButtonElement>): void => {
    const held = drag.current;
    if (held !== null && held.pointer === event.pointerId) {
      held.last = dragChatSize(held.wish, { x: event.clientX - held.x, y: event.clientY - held.y }, held.px, area);
      onResize(held.last, false);
    }
  };
  /**
   * Ends a drag, keeping a size: the one the release's own place gives, or, when the browser takes the pointer
   * away, the last one a move gave, since a cancel's coordinates say nothing of where the pointer was.
   *
   * @param event The release, or the browser taking the pointer away.
   * @param cancelled Whether the browser took the pointer away.
   */
  const onPointerEnd = (event: PointerEvent<HTMLButtonElement>, cancelled: boolean): void => {
    const held = drag.current;
    if (held === null || held.pointer !== event.pointerId) {
      return;
    }
    drag.current = null;
    setDragging(false);
    onActive(focused);
    onResize(cancelled ? held.last : dragChatSize(held.wish, { x: event.clientX - held.x, y: event.clientY - held.y }, held.px, area), true);
  };
  /**
   * The arrow keys resize; every other key is left alone.
   *
   * @param event The key press on the grip.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const next = keyChatSize(wish, event.key, event.shiftKey, area);
    if (next === null) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onResize(next, true);
  };
  const keyed = useSyncExternalStore(pageKeyHints.subscribe, pageKeyHints.getSnapshot, pageKeyHints.getSnapshot);
  return (
    <button
      ref={gripRef}
      type="button"
      className="ot-chat__grip"
      data-chat="grip"
      data-chat-dragging={dragging ? "true" : undefined}
      aria-label={say(keyed ? "chat.grip" : "chat.gripTouch")}
      aria-describedby={dragging || focused ? SIZE_LABEL_ID : undefined}
      onMouseDown={(event) => event.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => onPointerEnd(event, false)}
      onPointerCancel={(event) => onPointerEnd(event, true)}
      onKeyDown={onKeyDown}
      onFocus={(event) => {
        if (event.currentTarget.matches(":focus-visible")) {
          setFocused(true);
          onActive(true);
        }
      }}
      onBlur={() => {
        setFocused(false);
        onActive(dragging);
      }}
      style={{ width: logical(GRIP_SIZE), height: logical(GRIP_SIZE) }}
    >
      {DOTS.map(([left, top]) => (
        <span key={`${left}-${top}`} className="ot-chat__grip-dot" aria-hidden="true" style={{ left: logical(left), top: logical(top), width: logical(DOT), height: logical(DOT) }} />
      ))}
    </button>
  );
}

/**
 * The panel's size, shown beside its top-right corner while it is resized.
 *
 * @param props The `size` drawn.
 * @returns The label, a status region.
 */
export function SizeLabel({ size }: { readonly size: ChatSize }): ReactElement {
  return (
    <span className="ot-chat__size" id={SIZE_LABEL_ID} role="status" data-chat="size" style={{ left: logical(size.width + SIZE_LABEL_GAP) }}>
      {sizeLabel(size)}
    </span>
  );
}
